import React from 'react';
import { Link } from 'react-router-dom';
import { 
  UserGroupIcon, 
  DocumentTextIcon, 
  FolderIcon, 
  ChartBarIcon,
  CogIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

/**
 * Dashboard for Super Admin role
 * Shows system overview, user management, and administrative tools
 */
const SuperAdminDashboard = () => {
  const adminActions = [
    { name: 'Manage Users', href: '/users', icon: UserGroupIcon, color: 'bg-blue-500', description: 'Create and manage all user roles' },
    { name: 'Content Workflow', href: '/content-workflow', icon: DocumentTextIcon, color: 'bg-green-500', description: 'Oversee content creation process' },
    { name: 'System Analytics', href: '/analytics', icon: ChartBarIcon, color: 'bg-purple-500', description: 'View system-wide statistics' },
    { name: 'KIT Management', href: '/kits', icon: FolderIcon, color: 'bg-indigo-500', description: 'Manage all KITs and content' },
    { name: 'System Settings', href: '/settings', icon: CogIcon, color: 'bg-gray-500', description: 'Configure system parameters' },
    { name: 'Security & Audit', href: '/audit', icon: ShieldCheckIcon, color: 'bg-red-500', description: 'Security logs and audit trails' }
  ];

  const roleManagement = [
    { role: 'Content Supervisor', count: 2, href: '/users?role=content-supervisor', color: 'text-blue-600' },
    { role: 'Content Generator', count: 5, href: '/users?role=content-generator', color: 'text-green-600' },
    { role: 'Content Reviewer', count: 3, href: '/users?role=content-reviewer', color: 'text-purple-600' },
    { role: 'Legacy Analysts', count: 1, href: '/users?role=analyst', color: 'text-yellow-600' }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">Super Admin Dashboard</h1>
        <p className="text-gray-600 mt-1">System administration and user management</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {adminActions.map((action) => (
          <Link
            key={action.name}
            to={action.href}
            className="group relative bg-white p-6 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-start">
              <div className={`${action.color} p-3 rounded-lg`}>
                <action.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-gray-900 group-hover:text-blue-600">
                  {action.name}
                </h3>
                <p className="text-sm text-gray-600 mt-1">{action.description}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Role Management Overview */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Content Team Overview</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {roleManagement.map((role) => (
              <Link
                key={role.role}
                to={role.href}
                className="block p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
              >
                <div className="text-2xl font-bold text-gray-900">{role.count}</div>
                <div className={`text-sm font-medium ${role.color}`}>{role.role}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">System Health</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Content Workflow</span>
              <span className="text-sm font-medium text-green-600">Active</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">User Authentication</span>
              <span className="text-sm font-medium text-green-600">Operational</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Database</span>
              <span className="text-sm font-medium text-green-600">Connected</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Email Service</span>
              <span className="text-sm font-medium text-green-600">Available</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Admin Actions</h3>
          <div className="space-y-3">
            <div className="text-sm">
              <div className="font-medium text-gray-900">Content Supervisor created</div>
              <div className="text-gray-600">New user: jane.supervisor@example.com</div>
              <div className="text-xs text-gray-500">2 hours ago</div>
            </div>
            <div className="text-sm">
              <div className="font-medium text-gray-900">KIT published</div>
              <div className="text-gray-600">Physics MCQs KIT went live</div>
              <div className="text-xs text-gray-500">4 hours ago</div>
            </div>
            <div className="text-sm">
              <div className="font-medium text-gray-900">Bulk questions imported</div>
              <div className="text-gray-600">150 questions added to Chemistry</div>
              <div className="text-xs text-gray-500">1 day ago</div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Workflow Status */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Content Workflow Status</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">45</div>
              <div className="text-sm text-gray-600">Questions in Draft</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">23</div>
              <div className="text-sm text-gray-600">Pending Review</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">67</div>
              <div className="text-sm text-gray-600">Ready to Publish</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">8</div>
              <div className="text-sm text-gray-600">Need Attention</div>
            </div>
          </div>
        </div>
      </div>

      {/* Migration Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-3">🚀 Role Enhancement Complete</h3>
        <p className="text-blue-800 mb-3">
          The analyst role has been successfully enhanced into three specialized content roles:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-800">
          <div>
            <h4 className="font-medium mb-2">Content Supervisor</h4>
            <ul className="space-y-1">
              <li>• Manage KITs and workflow</li>
              <li>• Assign work to team</li>
              <li>• Approve final content</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-2">Content Generator</h4>
            <ul className="space-y-1">
              <li>• Create questions</li>
              <li>• Work with assigned KITs</li>
              <li>• Use creation workflows</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-2">Content Reviewer</h4>
            <ul className="space-y-1">
              <li>• Review question quality</li>
              <li>• Update question status</li>
              <li>• Provide feedback</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
